"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { afterScroll } from "@/lib/after-interaction";

const TARGET_SELECTOR =
  "main :is(h1,h2,h3,h4,h5,h6,p):not([data-no-reveal]):not([data-reveal-ready])";

const REVEAL_THRESHOLD = 0.2;
const STAGGER_S = 0.1;
const STAGGER_CAP = 2;

export function TextRevealObserver() {
  const pathname = usePathname();

  useEffect(() => {
    if (window.matchMedia("(max-width: 767px)").matches) {
      return;
    }

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const elementOrder = new Map<HTMLElement, number>();
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const start = () => {
      if (cancelled) return;

      const elements = Array.from(document.querySelectorAll<HTMLElement>(TARGET_SELECTOR)).filter(
        (element) => !element.closest("[data-no-reveal-scope]"),
      );

      if (elements.length === 0) {
        return;
      }

      elements.forEach((element, index) => {
        element.dataset.revealReady = "true";
        element.classList.add("soft-text-reveal");
        if (prefersReducedMotion) {
          element.classList.add("soft-text-reveal--reduced");
        }
        elementOrder.set(element, index);
      });

      observer = new IntersectionObserver(
        (entries) => {
          const visibleEntries = entries
            .filter((entry) => entry.isIntersecting)
            .sort(
              (a, b) =>
                (elementOrder.get(a.target as HTMLElement) ?? 0) -
                (elementOrder.get(b.target as HTMLElement) ?? 0),
            );

          const delayBySection = new Map<Element, number>();

          visibleEntries.forEach((entry) => {
            const element = entry.target as HTMLElement;
            const section = element.closest("section") ?? element.parentElement ?? element;
            const localIndex = delayBySection.get(section) ?? 0;
            delayBySection.set(section, localIndex + 1);

            const delay = prefersReducedMotion ? 0 : Math.min(localIndex, STAGGER_CAP) * STAGGER_S;
            element.style.setProperty("--reveal-delay", `${delay}s`);
            element.classList.add("soft-text-reveal-visible");
            observer?.unobserve(element);
          });
        },
        { threshold: REVEAL_THRESHOLD, rootMargin: "0px 0px -8% 0px" },
      );

      elements.forEach((element) => {
        observer?.observe(element);
      });
    };

    const detach = afterScroll(() => {
      requestAnimationFrame(() => requestAnimationFrame(start));
    }, 15000);

    return () => {
      cancelled = true;
      detach();
      observer?.disconnect();
    };
  }, [pathname]);

  return null;
}
